import * as React from 'react';
import * as _ from 'lodash';
import useHelpDocuments from '../../../hooks/useHelpDocuments';
import { Icon } from '@material-ui/core';
import * as styles from '../../help/help.module.scss';
import clsx from 'clsx';
import Tree, {
  TreeData,
  RenderItemParams,
  TreeSourcePosition,
  TreeDestinationPosition,
  moveItemOnTree
} from '@atlaskit/tree';
import useHelp from '../../../hooks/useHelp';
import { DragHandle, ExpandMore, ExpandLess } from '@material-ui/icons';
import { NavLink, useParams } from 'react-router-dom';
import Paths from '../../../models/enums/paths';

interface Props {
  documentsList: TreeData;
  setIsSearching: (searching: boolean) => void;
}

/**
 * Component rendering the drag and drop menu for help documents
 */
const SidePanel = ({ documentsList, setIsSearching }: Props) => {
  const {
    dispatch,
    changeActiveDoc,
    updateClientDocumentsSortOrder,
    updateGlobalDocumentsSortOrder,
    activeDocumentId,
    helpDocuments
  } = useHelpDocuments();
  const { clientId } = useParams<{ clientId: string }>();
  const {
    documentsWithoutRoot,
    findNextDocument,
    draggedSnippetGroupId,
    findChildren,
    getChildDocsToRenderEdit
  } = useHelp();
  const currentRoute = clientId ? `/clients/${clientId}/edit/help` : Paths.GlobalHelpDocuments;

  const onDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition) => {
    if (!destination) {
      return;
    }

    /** The previous help document tree */
    const prevState = documentsList;

    /** The new tree after the drag has ended */
    const newTree = moveItemOnTree(documentsList, source, destination);

    /** A copy of the source (includes parentId and index) */
    const sourceCopy = source;

    /** A copy of the destination (includes parentId and index) */
    const destinationCopy = destination;

    sourceCopy.parentId = sourceCopy.parentId === '-1' ? 0 : Number(sourceCopy.parentId);
    destinationCopy.parentId = destinationCopy.parentId === '-1' ? 0 : Number(destinationCopy.parentId);

    if (newTree.items[destinationCopy.parentId]) {
      newTree.items[destinationCopy.parentId].isExpanded = true;
    }

    dispatch({ helpDocuments: newTree });

    /** Array of tree items without the root document */
    const innerDocumentsWithoutRoot = documentsWithoutRoot(newTree);

    /** The document that comes after the document that was moved */
    const nextDocumentId = findNextDocument(destinationCopy.parentId, destination.index, newTree);

    /** SnippetGroupId of the document that was moved */
    const snippetGroupId = draggedSnippetGroupId(destinationCopy.index, destinationCopy.parentId, newTree);

    /** The full data of the document that was moved */
    const documentDragged = innerDocumentsWithoutRoot.find(doc => doc.data.snippetGroupId === snippetGroupId);

    /** All the children of the document that was moved */
    const allChildren = findChildren(snippetGroupId, [...documentDragged.children] as number[], newTree);

    if (!clientId) {
      updateGlobalDocumentsSortOrder(snippetGroupId, destinationCopy.parentId, allChildren, nextDocumentId, prevState);
    } else {
      updateClientDocumentsSortOrder(snippetGroupId, destinationCopy.parentId, allChildren, nextDocumentId, prevState);
    }
  };

  const onExpand = (itemId: number) => {
    const documents = _.cloneDeep(documentsList);

    documents.items[`${itemId}`].isExpanded = !documents.items[`${itemId}`].isExpanded;

    dispatch({ helpDocuments: documents });
  };

  /** Currently active help documents to be highlighted in renderItem */
  let activeItems = [];

  if (activeDocumentId) {
    activeItems = [...getChildDocsToRenderEdit(activeDocumentId, helpDocuments).map(doc => doc.snippetGroupId)];
  }

  const renderItem = ({ item, depth, provided, onExpand }: RenderItemParams) => {
    const active = activeItems && activeItems.includes(Number(item.id));

    return (
      <li
        className={clsx(styles.treeListItem, depth === 0 && styles.topLevel, active && styles.treeListItemActive)}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
      >
        <div className={styles.treeListItemContent}>
          <Icon className={styles.treeListCollapseIcon} onClick={() => onExpand(item.id)}>
            {item.hasChildren && item.isExpanded ? (
              <ExpandMore style={{ cursor: 'pointer' }} />
            ) : (
              item.hasChildren &&
              !item.isExpanded && <ExpandLess style={{ transform: 'rotate(90deg)', cursor: 'pointer' }} />
            )}
          </Icon>
          <NavLink
            className={styles.treeListItemLink}
            to={{
              pathname: `${currentRoute}/${item.data.snippetGroupId}`,
              state: { id: item.data.snippetGroupId }
            }}
            onClick={() => setIsSearching(false)}
            isActive={(param, location) => changeActiveDoc(param, location, item.data.snippetGroupId)}
          >
            {item.data && item.data.title}
          </NavLink>
          <Icon className={styles.treeListItemHandle}>
            <DragHandle />
          </Icon>
        </div>
      </li>
    );
  };

  if (!documentsList.items) {
    return <span />;
  }

  return (
    <ul className={styles.treeList}>
      <Tree
        tree={documentsList}
        renderItem={renderItem}
        offsetPerLevel={20}
        isDragEnabled
        onDragEnd={onDragEnd}
        isNestingEnabled
        onExpand={onExpand}
      />
    </ul>
  );
};
export default SidePanel;
